import { useCallback, useEffect, useRef } from "react";
import { driver } from "driver.js";

const STORAGE_PREFIX = "cliver_tour_seen_";

function hasSeenTour(tourId) {
  try {
    return localStorage.getItem(STORAGE_PREFIX + tourId) === "1";
  } catch {
    // localStorage indisponível (modo privado, storage bloqueado etc.) --
    // trata como "já visto" pra não insistir em mostrar o tour toda vez.
    return true;
  }
}

function markTourSeen(tourId) {
  try {
    localStorage.setItem(STORAGE_PREFIX + tourId, "1");
  } catch {
    // ignora silenciosamente -- sem persistência, o tour só reaparece
    // automaticamente (o botão "?" pra reabrir manualmente continua ok)
  }
}

/**
 * Hook reutilizável pra tours guiados com driver.js.
 *
 * - `tourId`: chave estável usada na persistência em localStorage (ex:
 *   "kanban", "automations", "settings-canais"). Cada tela/tour usa a sua.
 * - `steps`: array de passos no formato do driver.js
 *   (`{ element, popover: { title, description } }`). IMPORTANTE: precisa
 *   ser uma referência estável (useMemo) -- se mudar de referência a cada
 *   render, o efeito de auto-start abaixo re-executa à toa.
 * - `autoStart`: se true (padrão), dispara sozinho na primeira visita, ou
 *   seja, quando `tourId` ainda não foi marcado como visto no localStorage.
 *   Passe `false` enquanto os dados/elementos-alvo da tela ainda estão
 *   carregando, e `true` assim que a tela estiver pronta pra evitar tentar
 *   ancorar num elemento que ainda não existe no DOM.
 *
 * Retorna `{ start, hasSeen }`:
 * - `start()`: dispara o tour manualmente (ignora se já foi visto -- é
 *   exatamente isso que o botão "?" fixo de "reabrir tour" deve chamar).
 * - `hasSeen()`: consulta se este tour já foi visto (útil pra UI condicional).
 *
 * "Pular tour" já vem de fábrica: driver.js sempre mostra um botão de
 * fechar (showButtons inclui "close"), e o próprio X/clique fora também
 * fecha -- em qualquer um desses casos o onDestroyed abaixo marca como
 * visto, então pular conta como "visto" (não volta a aparecer sozinho).
 */
export function useProductTour(tourId, steps, { autoStart = true } = {}) {
  const driverRef = useRef(null);

  const start = useCallback(() => {
    if (!steps || steps.length === 0) return;
    const driverObj = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      nextBtnText: "Próximo",
      prevBtnText: "Voltar",
      doneBtnText: "Concluir",
      progressText: "{{current}} de {{total}}",
      overlayOpacity: 0.65,
      steps,
      onDestroyed: () => {
        markTourSeen(tourId);
      },
    });
    driverRef.current = driverObj;
    driverObj.drive();
  }, [tourId, steps]);

  useEffect(() => {
    if (!autoStart) return undefined;
    if (hasSeenTour(tourId)) return undefined;
    // Pequeno delay pra dar tempo dos elementos-alvo (que podem depender de
    // dados carregados assincronamente) já estarem no DOM antes do driver.js
    // tentar ancorar o primeiro passo.
    const timer = setTimeout(() => start(), 400);
    return () => clearTimeout(timer);
  }, [autoStart, tourId, start]);

  return {
    start,
    hasSeen: () => hasSeenTour(tourId),
  };
}
